import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import type { Relic } from '../../logic/collect/relics';
import { BoardPanel } from '../board/BoardPanel';
import { paintedPlate } from '../board/BrassButton';
import { TitlePlaque } from '../board/TitlePlaque';
import { animate, displayStyle, prefersReducedMotion } from '../board/ui';
import { glowTexture } from '../road/glow';

/**
 * `side`: a small board beside the reels with the three relics one above
 * another and the set's prize (wide screens). `row`: the three side by side
 * on the action row. `compact`: one bezel and "1/3" on the action row,
 * where nothing bigger fits (phones).
 */
export type RelicShelfMode = 'side' | 'row' | 'compact';

/** What the shelf draws with. */
export interface RelicShelfArt {
  /** The brass bezel each relic sits in. */
  bezel: () => Texture | null;
  /** A relic's painting, by alias. */
  relic: (alias: string) => Texture | null;
  /** The slot's reel floor, for the shelf's board. */
  floor: () => Texture | null;
  /** The prize's icons. */
  coin: () => Texture | null;
  gem: () => Texture | null;
}

/** The bezel art's hole is about 71% of its width; the relic fills it. The
 *  paintings leave a margin, so the relic is drawn a little bigger. */
const RELIC_OF_BEZEL = 0.66 * 1.32;
/** A missing relic: its shape, dark, in the socket, so the hunt is clear. */
export const MISSING_TINT = 0x6a5236;
export const MISSING_ALPHA = 0.75;
const GOLD_GLOW = 0xffd24a;
const NUMBER = new Intl.NumberFormat('en-GB');

interface Socket {
  root: Container;
  back: Graphics;
  glow: Sprite;
  relic: Sprite;
  bezel: Sprite;
}

/**
 * Reel collectibles' shelf: the slot's three relics in brass bezels, lit
 * when found and dark until then. Beside the reels on a wide screen, with
 * the prize the set pays; on the action row on a phone, folded to one
 * bezel with a "1/3" plate. A tap opens the relics board (the one-tap
 * explanation).
 */
export class RelicShelf extends Container {
  private readonly panel: BoardPanel;
  private readonly plaque = new TitlePlaque('RELICS', 13, 10);
  private readonly sockets: Socket[] = [];
  private readonly countPlate = new Container();
  private readonly countText: Text;
  private readonly prizeIcon = new Sprite();
  private readonly prizeText: Text;
  private plate: Container | null = null;
  private held: boolean[];
  private prize = { coins: 0, gems: 0 };
  private mode: RelicShelfMode = 'row';
  private size = 44;
  private readonly reduced = prefersReducedMotion();
  /** The relic last found, shown in the compact bezel. */
  private latest = -1;
  onTap: (() => void) | null = null;

  constructor(
    private readonly art: RelicShelfArt,
    private relics: Relic[],
  ) {
    super();
    this.held = relics.map(() => false);
    this.panel = new BoardPanel({ floor: art.floor(), radius: 12 });
    this.addChild(this.panel);
    for (let i = 0; i < 3; i++) this.sockets.push(this.makeSocket());
    this.prizeIcon.anchor.set(0.5);
    this.prizeText = new Text({ text: '', style: displayStyle(16) });
    this.prizeText.anchor.set(0, 0.5);
    this.addChild(this.prizeIcon, this.prizeText, this.plaque);
    this.countText = new Text({ text: '0/3', style: displayStyle(14) });
    this.countText.anchor.set(0.5);
    this.countPlate.addChild(this.countText);
    this.addChild(this.countPlate);
    this.eventMode = 'static';
    this.cursor = 'pointer';
    this.on('pointertap', () => this.onTap?.());
    this.paint();
  }

  /** The shelf as plain data, for the browser tests. */
  get snapshot(): { visible: boolean; mode: RelicShelfMode; held: boolean[]; count: string; prize: string } {
    return {
      visible: this.visible,
      mode: this.mode,
      held: [...this.held],
      count: this.countText.text,
      prize: this.prizeText.visible ? this.prizeText.text : '',
    };
  }

  /** A different slot's relics. */
  setRelics(relics: Relic[]): void {
    this.relics = relics;
    this.held = relics.map(() => false);
    this.latest = -1;
    this.paint();
  }

  /** Which relics are held. */
  setHeld(held: readonly boolean[]): void {
    this.held = this.relics.map((_, i) => held[i] === true);
    if (this.latest < 0 || !this.held[this.latest]) this.latest = this.held.lastIndexOf(true);
    this.paint();
  }

  /** What the set in progress pays (shown beside the reels). */
  setPrize(prize: { coins: number; gems: number }): void {
    this.prize = { coins: prize.coins, gems: prize.gems };
    this.paintPrize();
  }

  /** Width the shelf needs for `mode` with bezels `size` across. */
  static widthFor(mode: RelicShelfMode, size: number): number {
    if (mode === 'side') return Math.max(size + 24, 88);
    return mode === 'row' ? size * 3 + Math.round(size * 0.18) * 2 + 16 : size + 12;
  }

  /** Height the shelf needs for `mode` with bezels `size` across. */
  static heightFor(mode: RelicShelfMode, size: number): number {
    if (mode === 'side') return 18 + size * 3 + Math.round(size * 0.14) * 2 + 34;
    return mode === 'row' ? size + 16 : size + 24;
  }

  /** Place the shelf centred on (x, y), its bezels `size` across. */
  layout(mode: RelicShelfMode, x: number, y: number, size: number): void {
    this.mode = mode;
    this.size = Math.round(size);
    const s = this.size;
    const w = RelicShelf.widthFor(mode, s);
    const h = RelicShelf.heightFor(mode, s);
    this.panel.visible = mode !== 'compact';
    this.panel.resize(w, h);
    this.panel.setFloor(this.art.floor());
    this.plaque.visible = mode === 'side';
    this.plaque.position.set(w / 2, 0);
    this.prizeIcon.visible = this.prizeText.visible = mode === 'side';
    this.pivot.set(w / 2, h / 2);
    this.position.set(Math.round(x), Math.round(y));
    const gap = Math.round(s * (mode === 'side' ? 0.14 : 0.18));
    this.sockets.forEach((socket, i) => {
      socket.root.visible = mode !== 'compact' || i === 0;
      if (mode === 'side') socket.root.position.set(w / 2, 18 + s / 2 + i * (s + gap));
      else if (mode === 'row') socket.root.position.set(8 + s / 2 + i * (s + gap), h / 2);
      else socket.root.position.set(w / 2, s / 2 + 2);
      this.sizeSocket(socket, s);
    });
    if (mode === 'side') this.placePrize(w, h);
    this.countPlate.visible = mode === 'compact';
    if (mode === 'compact') {
      const pw = Math.max(Math.round(s * 1.15), Math.round(this.countText.width + 20));
      const ph = 20;
      this.plate?.destroy();
      this.plate = paintedPlate('wood', pw, ph);
      if (this.plate) {
        this.countPlate.addChildAt(this.plate, 0);
        this.plate.position.set(-pw / 2, -ph / 2);
      }
      this.countPlate.position.set(w / 2, s + 4);
    }
    this.hitArea = { contains: (px: number, py: number) => px >= -4 && py >= -12 && px <= w + 4 && py <= h + 4 };
    this.paint();
  }

  /** Where relic `piece` sits, in the parent's coordinates. */
  socketCentre(piece: number): { x: number; y: number } {
    const socket = this.mode === 'compact' ? this.sockets[0] : this.sockets[piece];
    const local = socket ? socket.root.position : { x: 0, y: 0 };
    return { x: this.x - this.pivot.x + local.x, y: this.y - this.pivot.y + local.y };
  }

  /** The middle of the shelf, in the parent's coordinates. */
  get centre(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  /** How big a bezel is on screen. */
  get bezelSize(): number {
    return this.size;
  }

  /** A relic lands in its socket: it lights and pulses. */
  async light(piece: number): Promise<void> {
    if (piece < 0 || piece >= this.held.length) return;
    this.held[piece] = true;
    this.latest = piece;
    this.paint();
    const socket = this.mode === 'compact' ? this.sockets[0] : this.sockets[piece];
    if (!socket || this.reduced) return;
    await animate(520, (t) => {
      const k = 1 + 0.28 * Math.sin(Math.PI * t);
      socket.root.scale.set(k);
      socket.glow.alpha = 0.35 + 0.65 * Math.sin(Math.PI * t);
    });
    socket.root.scale.set(1);
    this.paint();
  }

  /** The set is complete: every relic flashes gold. */
  async flashAll(): Promise<void> {
    if (this.reduced) return;
    await animate(1100, (t) => {
      const pulse = 0.5 + 0.5 * Math.sin(t * Math.PI * 4 - Math.PI / 2);
      for (const socket of this.sockets) {
        socket.glow.alpha = 0.3 + 0.7 * pulse;
        socket.root.scale.set(1 + 0.12 * pulse);
      }
    });
    for (const socket of this.sockets) socket.root.scale.set(1);
  }

  /** The set paid: the relics fade back to dark for the next one. */
  async empty(): Promise<void> {
    const from = [...this.held];
    this.held = this.held.map(() => false);
    this.latest = -1;
    if (this.reduced) {
      this.paint();
      return;
    }
    await animate(500, (t) => {
      this.sockets.forEach((socket, i) => {
        if (!from[i] && this.mode !== 'compact') return;
        socket.relic.alpha = 1 - t * (1 - MISSING_ALPHA);
        socket.glow.alpha = 1 - t;
      });
    });
    this.paint();
  }

  private makeSocket(): Socket {
    const root = new Container();
    const back = new Graphics();
    const glow = new Sprite(glowTexture());
    const relic = new Sprite();
    const bezel = new Sprite();
    for (const s of [glow, relic, bezel]) s.anchor.set(0.5);
    glow.tint = GOLD_GLOW;
    glow.blendMode = 'add';
    bezel.tint = 0xf2e2c0;
    root.addChild(back, glow, relic, bezel);
    this.addChild(root);
    return { root, back, glow, relic, bezel };
  }

  private sizeSocket(socket: Socket, s: number): void {
    socket.back.clear().circle(0, 0, s * 0.38).fill({ color: 0x140a03, alpha: 0.85 });
    socket.glow.width = socket.glow.height = s * 1.5;
    const bezel = this.art.bezel();
    if (bezel) socket.bezel.texture = bezel;
    socket.bezel.visible = bezel !== null;
    socket.bezel.width = socket.bezel.height = s;
  }

  private placePrize(w: number, h: number): void {
    const iconSize = 20;
    this.prizeIcon.width = this.prizeIcon.height = iconSize;
    const rowW = iconSize + 5 + this.prizeText.width;
    const left = (w - rowW) / 2;
    const y = h - 17;
    this.prizeIcon.position.set(left + iconSize / 2, y);
    this.prizeText.position.set(left + iconSize + 5, y);
  }

  private paintPrize(): void {
    const gems = this.prize.gems > 0 && this.prize.coins <= 0;
    this.prizeText.text = NUMBER.format(gems ? this.prize.gems : this.prize.coins);
    const icon = gems ? this.art.gem() : this.art.coin();
    if (icon) this.prizeIcon.texture = icon;
    if (this.mode === 'side') {
      const w = RelicShelf.widthFor('side', this.size);
      this.placePrize(w, RelicShelf.heightFor('side', this.size));
    }
  }

  private paint(): void {
    this.sockets.forEach((socket, i) => {
      const compact = this.mode === 'compact';
      const index = compact ? (this.latest >= 0 ? this.latest : this.relics.length - 1) : i;
      const relic = this.relics[index];
      const texture = relic ? this.art.relic(relic.art) : null;
      if (texture) socket.relic.texture = texture;
      socket.relic.visible = texture !== null;
      const box = this.size * RELIC_OF_BEZEL;
      socket.relic.scale.set(texture ? box / Math.max(1, texture.width, texture.height) : 1);
      const lit = compact ? this.latest >= 0 : this.held[i] === true;
      socket.relic.tint = lit ? 0xffffff : MISSING_TINT;
      socket.relic.alpha = lit ? 1 : MISSING_ALPHA;
      socket.glow.alpha = lit ? 0.35 : 0;
    });
    this.countText.text = `${this.held.filter(Boolean).length}/${this.held.length || 3}`;
    this.paintPrize();
  }
}
